import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { useJsApiLoader, GoogleMap, Polygon } from '@react-google-maps/api';

const MAP_CONTAINER_STYLE = { width: '100%', height: '800px' };
const DEFAULT_CENTER = { lat: 28.6139, lng: 77.209 }; // India
const DEFAULT_ZOOM = 16;

export type GeofenceGeoJSON = {
  type: 'Polygon';
  coordinates: number[][][]; // [ ring ] where ring is [ [lng, lat], ... ] closed
};

export interface GatePin {
  gateId: string;
  name?: string;
  latitude?: number;
  longitude?: number;
  /** Request area polygon (parent must be inside to request at this gate) */
  requestArea?: GeofenceGeoJSON | null;
}

interface GeofenceMapEditorProps {
  /** Existing geofence from school (GeoJSON Polygon) */
  initialGeofence?: GeofenceGeoJSON | null;
  /** Optional gate pickup points to show as pins on the map */
  gatePins?: GatePin[] | null;
  onSave: (geofence: GeofenceGeoJSON) => void;
  /** Called when user clears the existing geofence (saves null to server) */
  onClear?: () => void;
  /** When provided, shows a Cancel/Close button (e.g. in a dialog). Omit when embedded on page. */
  onCancel?: () => void;
  saving?: boolean;
  /** When false, hides Save/Clear/Draw actions (view-only). Default true. */
  canSave?: boolean;
}

/** Get center from GeoJSON polygon first ring for map focus */
function getCenterFromGeofence(geofence: GeofenceGeoJSON): { lat: number; lng: number } {
  const ring = geofence?.coordinates?.[0];
  if (!ring || !Array.isArray(ring) || ring.length === 0) return DEFAULT_CENTER;
  const first = ring[0];
  if (Array.isArray(first) && first.length >= 2) return { lng: first[0], lat: first[1] };
  return DEFAULT_CENTER;
}

const COORD_EPSILON = 1e-9;

/** Remove consecutive duplicate points from a ring (avoids merged/collapsed edges) */
function dedupeRing(ring: number[][]): number[][] {
  if (ring.length < 2) return ring;
  const out: number[][] = [ring[0]];
  for (let i = 1; i < ring.length; i++) {
    const prev = out[out.length - 1];
    const curr = ring[i];
    if (!prev || !curr) continue;
    const same =
      Math.abs(prev[0] - curr[0]) < COORD_EPSILON && Math.abs(prev[1] - curr[1]) < COORD_EPSILON;
    if (!same) out.push(curr);
  }
  return out;
}

/** Convert Google Maps LatLng path to GeoJSON ring (closed, no consecutive duplicates) */
function pathToGeoJSONRing(path: google.maps.LatLng[]): number[][] {
  let ring = path.map((ll) => [ll.lng(), ll.lat()]);
  const first = ring[0];
  const last = ring[ring.length - 1];
  if (first && last && (first[0] !== last[0] || first[1] !== last[1])) {
    ring.push([first[0], first[1]]);
  }
  ring = dedupeRing(ring);
  if (ring.length >= 2 && ring[0] && ring[ring.length - 1]) {
    const f = ring[0];
    const l = ring[ring.length - 1];
    if (Math.abs(f[0] - l[0]) >= COORD_EPSILON || Math.abs(f[1] - l[1]) >= COORD_EPSILON) {
      ring.push([f[0], f[1]]);
    }
  }
  return ring;
}

/** Extract a LatLng path from API geofence. GeoJSON Polygon: coordinates = [ ring ], ring = [ [lng,lat], ... ] */
function geofenceToPath(geofence: GeofenceGeoJSON | null | undefined): { lat: number; lng: number }[] | undefined {
  const coords = geofence?.coordinates;
  if (!coords || !Array.isArray(coords)) return undefined;
  const first = coords[0];
  let ring: number[][] = [];
  if (Array.isArray(first)) {
    if (first.length >= 2 && Array.isArray(first[0])) {
      ring = (first as unknown) as number[][];
    } else if (first.length >= 2 && typeof first[0] === 'number') {
      ring = (coords as unknown) as number[][];
    }
  }
  if (ring.length < 3) return undefined;
  let path: { lat: number; lng: number }[] = ring.map((c: number[]) => {
    const a = Number(c[0]);
    const b = Number(c[1]);
    const lng = Math.abs(a) <= 180 && Math.abs(b) <= 90 ? a : Math.abs(b) <= 180 ? b : a;
    const lat = Math.abs(a) <= 180 && Math.abs(b) <= 90 ? b : Math.abs(b) <= 180 ? a : b;
    return { lat, lng };
  });
  const p0 = path[0];
  const pLast = path[path.length - 1];
  if (p0 && pLast && (p0.lat !== pLast.lat || p0.lng !== pLast.lng)) path.push({ ...p0 });
  path = path.filter((p, i) => {
    if (i === 0) return true;
    const prev = path[i - 1];
    return prev && (Math.abs(prev.lat - p.lat) >= COORD_EPSILON || Math.abs(prev.lng - p.lng) >= COORD_EPSILON);
  });
  return path.length >= 3 ? path : undefined;
}

const GeofenceMapEditor: React.FC<GeofenceMapEditorProps> = ({
  initialGeofence,
  gatePins,
  onSave,
  onClear,
  onCancel,
  saving = false,
  canSave = true,
}) => {
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const mapRef = useRef<google.maps.Map | null>(null);
  const drawingManagerRef = useRef<google.maps.drawing.DrawingManager | null>(null);
  const existingPolygonRef = useRef<google.maps.Polygon | null>(null);
  const existingPolylineRef = useRef<google.maps.Polyline | null>(null);
  const gateMarkersRef = useRef<google.maps.Marker[]>([]);
  const lastDrawnPolygonRef = useRef<google.maps.Polygon | null>(null);
  const [pendingGeofence, setPendingGeofence] = useState<GeofenceGeoJSON | null>(null);
  const [drawingManagerReady, setDrawingManagerReady] = useState(false);
  const [mapReady, setMapReady] = useState(false);

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? '';
  const { isLoaded: scriptLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey,
    libraries: ['maps', 'drawing'],
  });
  const center = useMemo(
    () => (initialGeofence ? getCenterFromGeofence(initialGeofence) : DEFAULT_CENTER),
    [initialGeofence]
  );

  const existingPath = useMemo(() => geofenceToPath(initialGeofence), [initialGeofence]);

  const pendingPath = useMemo(() => {
    const ring = pendingGeofence?.coordinates?.[0];
    if (!ring || !Array.isArray(ring)) return undefined;
    return ring.map(([lng, lat]) => ({ lat, lng }));
  }, [pendingGeofence]);

  /** Gate request area paths for declarative Polygon rendering (orange zones) */
  const gateRequestAreaPaths = useMemo(() => {
    const gates = Array.isArray(gatePins) ? gatePins : [];
    return gates
      .map((g) => {
        const ra = g.requestArea;
        if (!ra || typeof ra !== 'object' || !Array.isArray(ra.coordinates) || ra.coordinates.length === 0) return null;
        return geofenceToPath(ra as GeofenceGeoJSON);
      })
      .filter((p): p is { lat: number; lng: number }[] => p != null && p.length >= 3);
  }, [gatePins]);

  const onPolygonComplete = useCallback((polygon: google.maps.Polygon) => {
    if (lastDrawnPolygonRef.current) {
      lastDrawnPolygonRef.current.setMap(null);
      lastDrawnPolygonRef.current = null;
    }
    const path = polygon.getPath();
    const arr = path.getArray();
    const ring = pathToGeoJSONRing(arr);
    if (ring.length >= 4) {
      setPendingGeofence({ type: 'Polygon', coordinates: [ring] });
      lastDrawnPolygonRef.current = polygon;
    } else {
      polygon.setMap(null);
    }
  }, []);

  const onMapLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;

      if (existingPolygonRef.current) {
        existingPolygonRef.current.setMap(null);
        existingPolygonRef.current = null;
      }
      if (existingPolylineRef.current) {
        existingPolylineRef.current.setMap(null);
        existingPolylineRef.current = null;
      }

      let bounds: google.maps.LatLngBounds | null = null;
      if (existingPath && existingPath.length >= 3) {
        const path = existingPath.map((p) => new google.maps.LatLng(p.lat, p.lng));
        const polygon = new google.maps.Polygon({
          paths: path,
          map,
          fillColor: '#1565c0',
          fillOpacity: 0.25,
          strokeColor: '#1565c0',
          strokeWeight: 2,
          zIndex: 1,
        });
        existingPolygonRef.current = polygon;
        const polyline = new google.maps.Polyline({
          path,
          map,
          strokeColor: '#0d47a1',
          strokeWeight: 3,
          zIndex: 2,
        });
        existingPolylineRef.current = polyline;
        bounds = new google.maps.LatLngBounds();
        path.forEach((p) => bounds!.extend(p));
      }

      gateMarkersRef.current.forEach((m) => { m.setMap(null); });
      gateMarkersRef.current = [];

      const gates = gatePins ?? [];
      const pins = gates.filter((g) => g.latitude != null && g.longitude != null && !Number.isNaN(g.latitude) && !Number.isNaN(g.longitude));
      if (pins.length > 0) {
        if (!bounds) bounds = new google.maps.LatLngBounds();
        pins.forEach((gate) => {
          const marker = new google.maps.Marker({
            position: { lat: gate.latitude!, lng: gate.longitude! },
            map,
            title: gate.name || gate.gateId || 'Gate',
            label: gate.name ? gate.name.charAt(0).toUpperCase() : undefined,
            zIndex: 10,
          });
          gateMarkersRef.current.push(marker);
          bounds!.extend(marker.getPosition()!);
        });
      }
      if (bounds) {
        map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
      }

      if (typeof google.maps.drawing === 'undefined') return;
      const manager = new google.maps.drawing.DrawingManager({
        map,
        drawingControl: true,
        drawingControlOptions: {
          position: google.maps.ControlPosition.TOP_CENTER,
          drawingModes: [google.maps.drawing.OverlayType.POLYGON],
        },
        polygonOptions: {
          fillColor: '#2e7d32',
          fillOpacity: 0.2,
          strokeWeight: 2,
          editable: true,
        },
      });
      google.maps.event.addListener(manager, 'polygoncomplete', onPolygonComplete);
      manager.setDrawingMode(google.maps.drawing.OverlayType.POLYGON);
      drawingManagerRef.current = manager;
      setDrawingManagerReady(true);
      setMapReady(true);
    },
    [existingPath, gatePins, onPolygonComplete]
  );

  // Redraw gate pins and request areas when gatePins changes (e.g. after setting in dialog or save + refetch)
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || typeof google === 'undefined') return;

    gateMarkersRef.current.forEach((m) => { m.setMap(null); });
    gateMarkersRef.current = [];

    const gates = Array.isArray(gatePins) ? gatePins : [];
    const pins = gates.filter((g) => g.latitude != null && g.longitude != null && !Number.isNaN(g.latitude) && !Number.isNaN(g.longitude));
    pins.forEach((gate) => {
      const marker = new google.maps.Marker({
        position: { lat: gate.latitude!, lng: gate.longitude! },
        map,
        title: gate.name || gate.gateId || 'Gate',
        label: gate.name ? gate.name.charAt(0).toUpperCase() : undefined,
        zIndex: 10,
      });
      gateMarkersRef.current.push(marker);
    });

  }, [mapReady, gatePins]);

  // Redraw existing school boundary when initialGeofence changes (e.g. after save when editing)
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || typeof google === 'undefined') return;

    if (existingPolygonRef.current) {
      existingPolygonRef.current.setMap(null);
      existingPolygonRef.current = null;
    }
    if (existingPolylineRef.current) {
      existingPolylineRef.current.setMap(null);
      existingPolylineRef.current = null;
    }

    if (existingPath && existingPath.length >= 3) {
      const path = existingPath.map((p) => new google.maps.LatLng(p.lat, p.lng));
      existingPolygonRef.current = new google.maps.Polygon({
        paths: path,
        map,
        fillColor: '#1565c0',
        fillOpacity: 0.25,
        strokeColor: '#1565c0',
        strokeWeight: 2,
        zIndex: 1,
      });
      existingPolylineRef.current = new google.maps.Polyline({
        path,
        map,
        strokeColor: '#0d47a1',
        strokeWeight: 3,
        zIndex: 2,
      });
    }
  }, [mapReady, existingPath]);

  useEffect(() => {
    return () => {
      if (lastDrawnPolygonRef.current) {
        lastDrawnPolygonRef.current.setMap(null);
        lastDrawnPolygonRef.current = null;
      }
      gateMarkersRef.current.forEach((m) => { m.setMap(null); });
      gateMarkersRef.current = [];
      if (drawingManagerRef.current) {
        drawingManagerRef.current.setMap(null);
        drawingManagerRef.current = null;
      }
      if (existingPolygonRef.current) {
        existingPolygonRef.current.setMap(null);
        existingPolygonRef.current = null;
      }
      if (existingPolylineRef.current) {
        existingPolylineRef.current.setMap(null);
        existingPolylineRef.current = null;
      }
    };
  }, []);

  const startDrawingPolygon = useCallback(() => {
    const manager = drawingManagerRef.current;
    if (manager && typeof google !== 'undefined' && google.maps?.drawing?.OverlayType?.POLYGON != null) {
      manager.setDrawingMode(google.maps.drawing.OverlayType.POLYGON);
    }
  }, []);

  const handleClearExisting = useCallback(() => {
    setClearConfirmOpen(true);
  }, []);

  const confirmClear = useCallback(() => {
    setClearConfirmOpen(false);
    onClear?.();
  }, [onClear]);

  const discardNewDrawing = useCallback(() => {
    if (lastDrawnPolygonRef.current) {
      lastDrawnPolygonRef.current.setMap(null);
      lastDrawnPolygonRef.current = null;
    }
    setPendingGeofence(null);
  }, []);

  const handleSave = useCallback(() => {
    const toSave = pendingGeofence || initialGeofence;
    if (
      toSave &&
      toSave.type === 'Polygon' &&
      Array.isArray(toSave.coordinates) &&
      toSave.coordinates[0] &&
      toSave.coordinates[0].length >= 3
    ) {
      if (lastDrawnPolygonRef.current) {
        lastDrawnPolygonRef.current.setMap(null);
        lastDrawnPolygonRef.current = null;
      }
      onSave(toSave);
    }
  }, [pendingGeofence, initialGeofence, onSave]);

  const hasGeofence = !!(
    (pendingGeofence && pendingGeofence.coordinates?.[0]?.length) ||
    (initialGeofence && initialGeofence.coordinates?.[0]?.length)
  );
  const saveEnabled = hasGeofence && !saving;

  if (!apiKey) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">
          Google Maps API key is not set. Add REACT_APP_GOOGLE_MAPS_API_KEY to your .env file.
        </Typography>
        {onCancel && <Button onClick={onCancel} sx={{ mt: 2 }}>Close</Button>}
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">Failed to load Google Maps: {loadError.message}</Typography>
        {onCancel && <Button onClick={onCancel} sx={{ mt: 2 }}>Close</Button>}
      </Box>
    );
  }

  const hasExisting = !!existingPath?.length;
  const hasNew = !!pendingPath?.length;
  const hasGateRequestAreas = gatePins?.some((g) => (g.requestArea?.coordinates?.[0]?.length ?? 0) >= 3);

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Draw the school boundary: click &quot;Draw new boundary&quot; then click on the map to add each corner. Double-click or click the first point again to close the shape.
      </Typography>

      {(hasExisting || hasNew || hasGateRequestAreas) && (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
          {hasExisting && (
            <Typography variant="body2" component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box component="span" sx={{ width: 16, height: 12, border: '2px dashed #1565c0', borderRadius: 0.5, bgcolor: 'rgba(21,101,192,0.15)' }} />
              School boundary
            </Typography>
          )}
          {hasNew && (
            <Typography variant="body2" component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box component="span" sx={{ width: 16, height: 12, bgcolor: 'rgba(46,125,50,0.4)', border: '2px solid #2e7d32', borderRadius: 0.5 }} />
              New boundary (unsaved)
            </Typography>
          )}
          {hasGateRequestAreas && (
            <Typography variant="body2" component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box component="span" sx={{ width: 16, height: 12, bgcolor: 'rgba(255,152,0,0.35)', border: '2px solid #e65100', borderRadius: 0.5 }} />
              Gate request area
            </Typography>
          )}
        </Box>
      )}

      {canSave && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={startDrawingPolygon}
            disabled={!scriptLoaded || !drawingManagerReady}
          >
            {drawingManagerReady ? 'Draw new boundary' : 'Preparing map…'}
          </Button>
          {hasExisting && onClear && (
            <Button variant="outlined" size="small" color="warning" onClick={handleClearExisting} disabled={saving}>
              Clear existing geofence
            </Button>
          )}
          {hasNew && (
            <Button variant="outlined" size="small" onClick={discardNewDrawing}>
              Discard new drawing
            </Button>
          )}
        </Box>
      )}

      {!scriptLoaded ? (
        <Box sx={{ height: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading map…</Box>
      ) : (
        <GoogleMap
          mapContainerStyle={MAP_CONTAINER_STYLE}
          center={center}
          zoom={DEFAULT_ZOOM}
          onLoad={onMapLoad}
          options={{
            mapTypeControl: true,
            streetViewControl: false,
            fullscreenControl: true,
            zoomControl: true,
          }}
        >
          {pendingPath && (
            <Polygon
              paths={pendingPath}
              options={{
                fillColor: '#2e7d32',
                fillOpacity: 0.35,
                strokeColor: '#2e7d32',
                strokeWeight: 3,
                editable: false,
                zIndex: 2,
              }}
            />
          )}
          {gateRequestAreaPaths.map((path, i) => (
            <Polygon
              key={`gate-request-${i}`}
              paths={path}
              options={{
                fillColor: '#ff9800',
                fillOpacity: 0.35,
                strokeColor: '#e65100',
                strokeWeight: 3,
                zIndex: 5,
              }}
            />
          ))}
        </GoogleMap>
      )}
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
        {onCancel && <Button onClick={onCancel} disabled={saving}>Cancel</Button>}
        {canSave && (
          <Button variant="contained" onClick={handleSave} disabled={!saveEnabled}>
            {saving ? 'Saving…' : hasNew ? 'Save new geofence' : 'Save geofence'}
          </Button>
        )}
      </Box>

      <Dialog open={clearConfirmOpen} onClose={() => setClearConfirmOpen(false)}>
        <DialogTitle>Clear existing geofence?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will remove the current school boundary. You can draw a new one after. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearConfirmOpen(false)}>Keep it</Button>
          <Button onClick={confirmClear} color="warning" variant="contained" autoFocus>
            Clear geofence
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GeofenceMapEditor;
